/* Teak theme behaviour: the scroll film, the chat sheet, the answer drawers and
 * the WhatsApp dock. No dependencies. Nothing here stores or sends a visitor's
 * answers anywhere. */
(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  /* ---------- Scroll film ---------- */

  function initFilm(root) {
    const layers = [...root.querySelectorAll("[data-layer]")];
    const chapters = [...root.querySelectorAll("[data-chapter]")];
    const routes = [...root.querySelectorAll("[data-jump]")];
    if (!layers.length || layers.length !== chapters.length) return;

    const phone = window.matchMedia("(max-width: 860px), (hover: none) and (pointer: coarse)");
    const clips = layers.map(() => ({ video: null, blob: null, loading: false, ready: false, seeking: false, frac: 0 }));
    let active = -1;
    let queued = false;

    const sourceFor = (i) => (phone.matches ? layers[i].dataset.mobileClip : layers[i].dataset.clip);

    function paint(i) {
      layers[i].dataset.painted = "true";
    }

    function seek(i) {
      const clip = clips[i];
      const video = clip.video;
      if (!video || !clip.ready || clip.seeking) return;
      const end = Math.max((video.duration || 0) - 0.04, 0);
      const time = clamp(clip.frac * end, 0.001, end || 0.001);
      if (Math.abs(video.currentTime - time) < 0.012) return;
      clip.seeking = true;
      video.currentTime = time;
    }

    async function load(i) {
      if (reduceMotion || i < 0 || i >= clips.length) return;
      const clip = clips[i];
      if (clip.loading || clip.video) return;
      clip.loading = true;
      const url = sourceFor(i);
      let src = url;
      try {
        // A Blob URL makes every seek local, so scrubbing stays smooth.
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error(String(response.status));
        clip.blob = URL.createObjectURL(await response.blob());
        src = clip.blob;
      } catch {
        src = url;
      }
      const video = document.createElement("video");
      video.className = "t-film__video";
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = "auto";
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("aria-hidden", "true");
      video.tabIndex = -1;
      video.addEventListener("loadeddata", () => {
        // iOS only paints seeked frames after the element has played once.
        const primed = video.play();
        const ready = () => {
          video.pause();
          clip.ready = true;
          seek(i);
        };
        if (primed && primed.then) primed.then(ready, ready);
        else ready();
      }, { once: true });
      video.addEventListener("seeked", () => {
        clip.seeking = false;
        paint(i);
        seek(i);
      });
      video.src = src;
      layers[i].appendChild(video);
      clip.video = video;
    }

    function setActive(i) {
      if (i === active) return;
      active = i;
      layers.forEach((layer, index) => {
        layer.style.opacity = index === i ? "1" : "0";
      });
      routes.forEach((button, index) => {
        if (index === i) button.setAttribute("aria-current", "step");
        else button.removeAttribute("aria-current");
      });
      load(i);
      load(i + 1);
      load(i - 1);
    }

    function update() {
      queued = false;
      const viewport = window.innerHeight;
      const box = root.getBoundingClientRect();
      const travel = Math.max(box.height - viewport, 1);
      root.style.setProperty("--t-film-progress", clamp(-box.top / travel, 0, 1).toFixed(4));

      let current = 0;
      chapters.forEach((chapter, index) => {
        if (chapter.getBoundingClientRect().top <= 1) current = index;
      });
      setActive(current);

      const rect = chapters[current].getBoundingClientRect();
      const last = current === chapters.length - 1;
      const span = Math.max(rect.height - (last ? viewport : 0), 1);
      clips[current].frac = clamp(-rect.top / span, 0, 1);
      seek(current);
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    routes.forEach((button) => {
      button.addEventListener("click", () => {
        const chapter = chapters[Number(button.dataset.jump)];
        const top = chapter.getBoundingClientRect().top + window.scrollY + 2;
        window.scrollTo({ top, behavior: reduceMotion ? "auto" : "smooth" });
      });
    });

    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    window.addEventListener("pagehide", () => {
      clips.forEach((clip) => clip.blob && URL.revokeObjectURL(clip.blob));
    });
    update();
  }

  /* ---------- Chat sheet ---------- */

  const FIRST = {
    question: "What's bothering you most?",
    options: ["Tired all the time", "Less drive than I used to have", "Flat, not myself", "Hard to say, just not right"],
  };
  const SECOND = {
    question: "Do you have a recent blood test?",
    options: ["Yes, in the last three months", "Yes, but it's older", "Not yet"],
  };

  function initChat(root) {
    const steps = root.querySelector("[data-chat-steps]");
    const panel = root.querySelector('[role="dialog"]');
    const waMark = root.querySelector("[data-wa-mark]");
    const waitlist = root.querySelector("[data-waitlist]");
    const whatsapp = (root.dataset.wa || "").trim();
    const shortLink = whatsapp.includes("/message/");
    const state = { first: null, second: null, copied: false };
    let returnTo = null;

    const escapeHtml = (text) =>
      text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
    // Lower-case only the first letter, so "I" keeps its capital.
    const lead = (text) => text.charAt(0).toLowerCase() + text.slice(1);
    const mark = () => (waMark ? waMark.innerHTML : "");

    function message() {
      return `Hi Teak. Mostly: ${lead(state.first)}. Blood test: ${lead(state.second)}.`;
    }

    function href(text) {
      if (!whatsapp) return "";
      if (shortLink) return whatsapp;
      return `${whatsapp}${whatsapp.includes("?") ? "&" : "?"}text=${encodeURIComponent(text)}`;
    }

    function options(list, key) {
      return `<div class="t-chat__opts">${list
        .map((option) => `<button class="t-chat__opt" data-answer="${key}" data-value="${escapeHtml(option)}" type="button">${escapeHtml(option)}</button>`)
        .join("")}</div>`;
    }

    function picked(value, key) {
      return `<p class="t-chat__picked"><span>${escapeHtml(value)}</span><button class="t-chat__change" data-change="${key}" type="button">Change</button></p>`;
    }

    function render() {
      let html = `<li data-step><p class="t-chat__q">${FIRST.question}</p>${
        state.first ? picked(state.first, "first") : options(FIRST.options, "first")
      }</li>`;
      if (state.first) {
        html += `<li data-step><p class="t-chat__q">${SECOND.question}</p>${
          state.second ? picked(state.second, "second") : options(SECOND.options, "second")
        }</li>`;
      }
      if (state.first && state.second && whatsapp) {
        const text = message();
        const link = href(text);
        const howto =
          state.second === "Not yet"
            ? "No test yet is fine. We'll tell you exactly what to ask your GP or a private lab for."
            : "Then attach your blood test in the chat: tap + on iPhone or the paperclip on Android, and choose Document for the PDF or Camera for a photo of the printout.";
        html += `<li data-step><p class="t-chat__q">Your first message</p><blockquote class="t-chat__msg">${escapeHtml(text)}</blockquote><p class="t-chat__howto">${howto}</p>`;
        if (link) {
          if (shortLink) {
            html += `<button class="t-chat__copy" data-copy type="button">${state.copied ? "Copied" : "Copy message"}</button>`;
          }
          html += `<a class="t-chat__go" href="${escapeHtml(link)}" rel="noopener noreferrer" target="_blank">${mark()}Open WhatsApp</a>`;
          html += `<p class="t-chat__note">${
            shortLink
              ? "Paste it into the chat. Nothing sends until you press send."
              : "WhatsApp opens with this written for you. Nothing sends until you press send."
          }</p>`;
        }
        html += "</li>";
      }
      steps.innerHTML = html;
      // No WhatsApp line yet: the last step becomes the email waitlist.
      if (waitlist) waitlist.hidden = !(state.first && state.second) || Boolean(whatsapp);
    }

    function focusNewest() {
      const all = steps.querySelectorAll("[data-step]");
      const newest = all[all.length - 1];
      const inWaitlist = waitlist && !waitlist.hidden ? waitlist.querySelector("input[type=email]") : null;
      const target = inWaitlist || (newest && newest.querySelector("button, a[href]")) || panel.querySelector(".t-chat__x");
      window.setTimeout(() => target && target.focus(), 60);
    }

    function open() {
      returnTo = document.activeElement;
      render();
      root.dataset.open = "true";
      root.inert = false;
      root.removeAttribute("inert");
      document.documentElement.classList.add("t-locked");
      focusNewest();
    }

    function close() {
      root.dataset.open = "false";
      root.inert = true;
      root.setAttribute("inert", "");
      document.documentElement.classList.remove("t-locked");
      if (returnTo && returnTo.focus) returnTo.focus();
    }

    document.addEventListener("click", (event) => {
      const opener = event.target.closest("[data-open-chat]");
      if (opener) {
        event.preventDefault();
        open();
      }
    });

    root.addEventListener("click", async (event) => {
      const target = event.target.closest("button, a");
      if (!target) return;
      if (target.hasAttribute("data-chat-close")) {
        close();
      } else if (target.dataset.answer) {
        state[target.dataset.answer] = target.dataset.value;
        state.copied = false;
        render();
        focusNewest();
      } else if (target.dataset.change) {
        if (target.dataset.change === "first") state.first = null;
        state.second = null;
        state.copied = false;
        render();
        focusNewest();
      } else if (target.hasAttribute("data-copy")) {
        try {
          await navigator.clipboard.writeText(message());
          state.copied = true;
        } catch {
          state.copied = false;
        }
        render();
      }
    });

    if (root.querySelector("[data-waitlist-done]")) {
      state.first = state.first || "Hard to say, just not right";
      state.second = state.second || "Not yet";
      open();
      steps.innerHTML = "";
      waitlist.hidden = false;
    }

    document.addEventListener("keydown", (event) => {
      if (root.dataset.open !== "true") return;
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...panel.querySelectorAll("button, a[href], input:not([type=hidden])")].filter((el) => !el.closest("[hidden]"));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------- Answer drawers ---------- */

  function initAccordion(root) {
    root.addEventListener("click", (event) => {
      const button = event.target.closest("button[aria-controls]");
      if (!button) return;
      const opening = button.getAttribute("aria-expanded") !== "true";
      root.querySelectorAll("button[aria-controls]").forEach((other) => {
        const isThis = other === button;
        const expanded = isThis && opening;
        other.setAttribute("aria-expanded", String(expanded));
        const region = document.getElementById(other.getAttribute("aria-controls"));
        if (region) region.hidden = !expanded;
      });
    });
  }

  /* ---------- WhatsApp dock ---------- */

  function initDock(dock) {
    // The dock waits until the opening section (film or photo hero) is behind you.
    const film = document.querySelector("[data-teak-film], .t-hero");
    const closing = document.getElementById("start");
    const show = (visible) => {
      dock.dataset.show = visible ? "true" : "false";
      dock.setAttribute("aria-hidden", visible ? "false" : "true");
      dock.tabIndex = visible ? 0 : -1;
    };
    if (!film) {
      show(true);
      return;
    }
    // A plain scroll check (not IntersectionObserver) so it behaves the same
    // in every browser: show once the film is behind you, hide over the
    // closing band, which carries its own WhatsApp button.
    let queued = false;
    const update = () => {
      queued = false;
      const viewport = window.innerHeight;
      const pastFilm = film.getBoundingClientRect().bottom < 0;
      let overClosing = false;
      if (closing) {
        const box = closing.getBoundingClientRect();
        overClosing = box.top < viewport && box.bottom > 0;
      }
      show(pastFilm && !overClosing);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    update();
  }

  /* ---------- Testosterone unit converter (runs in the browser; nothing is stored or sent) ---------- */

  const NMOL_TO_NGDL = 28.84;

  function initConverter(slot) {
    slot.innerHTML =
      '<div class="t-tool" role="group" aria-labelledby="t-tool-title">' +
      '<p class="t-guide__label" id="t-tool-title">Convert a testosterone result</p>' +
      '<div class="t-tool__row">' +
      '<label>Your number<input inputmode="decimal" min="0" step="any" type="number" data-conv-value placeholder="e.g. 14.2"></label>' +
      '<label>Unit on your report<select data-conv-unit><option value="nmol">nmol/L (Australia, NZ)</option><option value="ngdl">ng/dL (US)</option></select></label>' +
      "</div>" +
      '<p class="t-tool__out" aria-live="polite" data-conv-out>Enter a number</p>' +
      '<p class="t-tool__note">1 nmol/L = 28.84 ng/dL. The conversion happens on your device and nothing you type is saved or sent. A number on its own is not a diagnosis; ranges differ by lab.</p>' +
      "</div>";
    const input = slot.querySelector("[data-conv-value]");
    const unit = slot.querySelector("[data-conv-unit]");
    const out = slot.querySelector("[data-conv-out]");
    const render = () => {
      const value = parseFloat(String(input.value).replace(",", "."));
      if (!Number.isFinite(value) || value < 0) {
        out.textContent = "Enter a number";
        return;
      }
      out.textContent =
        unit.value === "nmol"
          ? value + " nmol/L = " + Math.round(value * NMOL_TO_NGDL) + " ng/dL"
          : value + " ng/dL = " + (value / NMOL_TO_NGDL).toFixed(1) + " nmol/L";
    };
    input.addEventListener("input", render);
    unit.addEventListener("change", render);
  }

  function start() {
    document.querySelectorAll('[data-teak-tool="converter"]').forEach(initConverter);
    document.querySelectorAll("[data-print]").forEach((b) => b.addEventListener("click", () => window.print()));
    document.querySelectorAll("[data-teak-film]").forEach(initFilm);
    const chat = document.querySelector("[data-chat]");
    if (chat) initChat(chat);
    document.querySelectorAll("[data-accordion]").forEach(initAccordion);
    const dock = document.querySelector("[data-dock]");
    if (dock) initDock(dock);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
